/**
 * ANC Copilot — operador cognitivo contextual en drawer lateral.
 *
 * Cableado al **planner determinista** de la Capa (`../lib/copilot`): cada
 * consulta ensambla el contexto vía el Motor_Contexto_IA (única puerta de
 * lectura, R6.5), interpreta un Plan determinista (navegación, causa raíz,
 * consulta geo-operacional, acción multi-entidad o aclaración) y lo presenta
 * con su Explainability (R6.8). Las operaciones multi-entidad se **proponen
 * sin invocar** ninguna acción hasta recibir confirmación humana explícita
 * (R6.4); la propuesta caduca al expirar su ventana (R6.10).
 *
 * Mantiene historial de la conversación y ofrece preguntas sugeridas + enlaces
 * de drill-down a las vistas relevantes.
 */
import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { Icon } from "../components/Icon";
import { useCopilot } from "../system/CopilotContext";
import { useAuth } from "../auth/AuthContext";
import {
  configurarInsightsLoader,
  ejecutarConsultaCopilot,
  obtenerCopilot,
  type RespuestaCopilotUI,
} from "../lib/copilot";
import { loadInsightsBundle } from "../lib/insightsRuntime";

interface Turn {
  role: "user" | "assistant";
  text: string;
  bullets?: string[];
  refs?: Array<{ label: string; to: string }>;
  /** Id de propuesta multi-entidad pendiente de confirmación (R6.4). */
  propuestaId?: string;
  /** Estado de la propuesta presentada (para la UI de confirmación). */
  propuestaEstado?: "pendiente" | "resuelta";
}

// Inyecta el cargador de Insights (datos reales) para resolver consultas de
// causa raíz citando evidencia (R6.2), sin acoplar el planner al loader.
configurarInsightsLoader(async () => (await loadInsightsBundle()).insights);

const SUGGESTED = [
  "¿Por qué aumentó el NRW en DMA-003?",
  "¿Qué casos tienen mayor ROI?",
  "¿Qué medidores probablemente fallarán este mes?",
  "Resume las alertas críticas.",
];

export function Copilot(): JSX.Element | null {
  const { open, closeCopilot, seedQuestion, clearSeed } = useCopilot();
  const { activeRole } = useAuth();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState("");
  const bodyRef = useRef<HTMLDivElement>(null);

  // Actor de la invocación: el rol activo (permission-aware en el Registro, R6.6).
  const actor = activeRole ?? "Operador";

  const ask = useMutation({
    mutationFn: (question: string): Promise<RespuestaCopilotUI> =>
      ejecutarConsultaCopilot(question, actor),
  });

  const submit = (question: string) => {
    if (!question.trim()) return;
    setTurns((t) => [...t, { role: "user", text: question }]);
    setInput("");
    ask.mutate(question, {
      onSuccess: (res) => {
        setTurns((t) => [
          ...t,
          {
            role: "assistant",
            text: res.answer,
            bullets: res.bullets,
            refs: res.refs,
            ...(res.propuesta
              ? { propuestaId: res.propuesta.propuesta_id, propuestaEstado: "pendiente" as const }
              : {}),
          },
        ]);
      },
    });
  };

  // Confirmación humana explícita de una propuesta multi-entidad (R6.4).
  const confirmarPropuesta = async (turnIndex: number, propuestaId: string) => {
    const res = await obtenerCopilot().confirmar(propuestaId, actor);
    setTurns((t) =>
      t.map((turn, i) => (i === turnIndex ? { ...turn, propuestaEstado: "resuelta" as const } : turn)),
    );
    const detalle = res.ok
      ? `Confirmado: ${res.value.length} acción(es) enviadas al Registro_Acciones_Semanticas (gate de permisos/HITL).`
      : `No se pudo confirmar la propuesta: ${res.error}`;
    setTurns((t) => [...t, { role: "assistant", text: detalle }]);
  };

  // Cancelación explícita (preserva el estado, no invoca nada).
  const cancelarPropuesta = (turnIndex: number, propuestaId: string) => {
    obtenerCopilot().cancelar(propuestaId);
    setTurns((t) =>
      t.map((turn, i) => (i === turnIndex ? { ...turn, propuestaEstado: "resuelta" as const } : turn)),
    );
    setTurns((t) => [...t, { role: "assistant", text: "Propuesta cancelada. No se ejecutó ninguna acción." }]);
  };

  // Seed question coming from command palette / contextual actions.
  useEffect(() => {
    if (open && seedQuestion) {
      submit(seedQuestion);
      clearSeed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seedQuestion]);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: "smooth" });
  }, [turns, ask.isPending]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) closeCopilot();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, closeCopilot]);

  if (!open) return null;

  return (
    <>
      <div className="drawer-overlay" onClick={closeCopilot} />
      <aside className="drawer" role="dialog" aria-label="ANC Copilot">
        <div className="drawer__header">
          <div className="hstack" style={{ gap: 10 }}>
            <span
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                background: "linear-gradient(135deg, #2db5e0, #073a52)",
                display: "grid",
                placeItems: "center",
                color: "#fff",
              }}
            >
              <Icon name="flask" size={15} />
            </span>
            <div>
              <div style={{ fontWeight: 600, color: "var(--c-text-strong)" }}>ANC Copilot</div>
              <div className="text-faint" style={{ fontSize: 11 }}>
                Analista NRW contextual
              </div>
            </div>
          </div>
          <button className="btn btn--ghost btn--icon" onClick={closeCopilot} aria-label="Cerrar">
            <Icon name="logout" size={14} />
          </button>
        </div>

        <div className="drawer__body" ref={bodyRef} style={{ flex: 1 }}>
          {turns.length === 0 && (
            <div className="vstack" style={{ gap: 10 }}>
              <p className="text-muted" style={{ fontSize: 13 }}>
                Pregúntame sobre la operación. Interpreto anomalías, ROI de casos,
                riesgo de medidores y tendencias de NRW.
              </p>
              <div className="card__title" style={{ fontSize: 11 }}>Preguntas sugeridas</div>
              <div className="vstack" style={{ gap: 6 }}>
                {SUGGESTED.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="btn btn--ghost"
                    style={{ justifyContent: "flex-start", width: "100%" }}
                    onClick={() => submit(s)}
                  >
                    <Icon name="search" size={12} /> {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="vstack" style={{ gap: 12 }}>
            {turns.map((t, i) => (
              <div key={i}>
                {t.role === "user" ? (
                  <div
                    style={{
                      background: "var(--c-primary-50)",
                      color: "var(--c-primary-700)",
                      padding: "8px 12px",
                      borderRadius: 10,
                      fontSize: 13,
                      alignSelf: "flex-end",
                      maxWidth: "90%",
                      marginLeft: "auto",
                    }}
                  >
                    {t.text}
                  </div>
                ) : (
                  <div
                    style={{
                      background: "var(--c-surface)",
                      border: "1px solid var(--c-border)",
                      padding: "10px 12px",
                      borderRadius: 10,
                      fontSize: 13,
                    }}
                  >
                    <div style={{ color: "var(--c-text)", marginBottom: t.bullets?.length ? 8 : 0 }}>
                      {t.text}
                    </div>
                    {t.bullets && (
                      <ul style={{ margin: "0 0 6px", paddingLeft: 16, color: "var(--c-text-muted)" }}>
                        {t.bullets.map((b, bi) => (
                          <li key={bi} style={{ fontSize: 12.5, marginBottom: 3 }}>
                            {b}
                          </li>
                        ))}
                      </ul>
                    )}
                    {t.refs && t.refs.length > 0 && (
                      <div className="hstack" style={{ flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                        {t.refs.map((r) => (
                          <Link
                            key={r.to + r.label}
                            to={r.to}
                            className="btn btn--ghost"
                            onClick={closeCopilot}
                          >
                            {r.label} <Icon name="chevron-right" size={11} />
                          </Link>
                        ))}
                      </div>
                    )}
                    {t.propuestaId && t.propuestaEstado === "pendiente" && (
                      <div className="hstack" style={{ flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                        <button
                          type="button"
                          className="btn btn--primary"
                          onClick={() => void confirmarPropuesta(i, t.propuestaId!)}
                        >
                          Confirmar ejecución
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost"
                          onClick={() => cancelarPropuesta(i, t.propuestaId!)}
                        >
                          Cancelar
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {ask.isPending && (
              <div className="text-faint" style={{ fontSize: 12.5 }}>
                ANC Copilot está analizando…
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: 16, borderTop: "1px solid var(--c-border)" }}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit(input);
            }}
            className="hstack"
            style={{ gap: 8 }}
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Pregunta sobre la operación…"
              style={{
                flex: 1,
                background: "var(--c-surface-2)",
                border: "1px solid var(--c-border)",
                borderRadius: 8,
                padding: "9px 12px",
                color: "var(--c-text-strong)",
                font: "inherit",
                fontSize: 13,
                outline: "none",
              }}
            />
            <button type="submit" className="btn btn--primary" disabled={!input.trim()}>
              <Icon name="chevron-right" size={14} />
            </button>
          </form>
        </div>
      </aside>
    </>
  );
}
